import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import {
  CalendarDaysIcon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import Calendar from 'react-calendar';
import './DatepickerStyles.css';

/**
 * Props:
 * - selectedDates: { from: 'YYYY-MM-DD' | '', to: 'YYYY-MM-DD' | '' }
 * - onDateChange: ( { from: 'YYYY-MM-DD' | '', to: 'YYYY-MM-DD' | '' } ) => void
 * - placeholder?: string ('When' fallback)
 * - minDate?: Date | null
 */
const DatePicker = ({
  selectedDates,
  onDateChange,
  placeholder = 'When',
  minDate = null,
}) => {
  const { t } = useTranslation();

  // local state mirrors the controlled prop for the calendar widget
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    const f = selectedDates?.from
      ? moment(selectedDates.from, 'YYYY-MM-DD').toDate()
      : null;
    const t = selectedDates?.to
      ? moment(selectedDates.to, 'YYYY-MM-DD').toDate()
      : null;
    setFromDate(f);
    setToDate(t);
  }, [selectedDates?.from, selectedDates?.to]);

  const hasRange = useMemo(() => !!fromDate && !!toDate, [fromDate, toDate]);

  const label = useMemo(() => {
    if (!fromDate && !toDate) return placeholder;
    const f = moment(fromDate || toDate).format('MMM DD');
    const t = moment(toDate || fromDate).format('MMM DD');
    return `${f} – ${t}`;
  }, [fromDate, toDate, placeholder]);

  const commit = (from, to) => {
    const start = from || to;
    const end = to || from;
    onDateChange({
      from: start ? moment(start).format('YYYY-MM-DD') : '',
      to: end ? moment(end).format('YYYY-MM-DD') : '',
    });
  };

  const handleCalendarChange = (value) => {
    // react-calendar with selectRange=true returns [from, to]
    const [f, t] = value;
    setFromDate(f);
    setToDate(t);
    if (f && t) commit(f, t);
  };

  const clear = () => {
    setFromDate(null);
    setToDate(null);
    onDateChange({ from: '', to: '' });
  };

  // Quick presets
  const today = new Date();
  const startOfWeek = (d) => {
    const date = new Date(d);
    const day = date.getDay(); // 0 Sun .. 6 Sat
    const diff = day === 0 ? -2 : 5 - day; // Fri index = 5
    const fri = new Date(date);
    fri.setDate(date.getDate() + diff);
    const sun = new Date(fri);
    sun.setDate(fri.getDate() + 2);
    return [fri, sun];
  };

  const presets = [
    { key: 'today', label: t('datepicker.today'), range: [today, today] },
    {
      key: 'tomorrow',
      label: t('datepicker.tomorrow'),
      range: [
        moment(today).add(1, 'day').toDate(),
        moment(today).add(1, 'day').toDate(),
      ],
    },
    {
      key: 'next7',
      label: t('datepicker.next7'),
      range: [today, moment(today).add(6, 'day').toDate()],
    },
    {
      key: 'weekend',
      label: t('datepicker.weekend'),
      range: startOfWeek(today),
    },
  ];

  const applyPreset = (range) => {
    const [f, t] = range;
    setFromDate(f);
    setToDate(t);
    commit(f, t);
  };

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <div className="relative">
            <PopoverButton
              className={`flex items-center justify-between w-full h-11 px-3 rounded-full bg-white text-sm outline-none border ${
                open ? 'border-gray-400' : 'border-gray-200'
              } hover:border-gray-300 transition`}
            >
              <div className="flex items-center min-w-0">
                <CalendarDaysIcon className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                <span
                  className={`truncate ${hasRange ? 'text-gray-900' : 'text-gray-400'}`}
                  title={label}
                >
                  {label}
                </span>
              </div>

              <ChevronDownIcon
                className={`h-4 w-4 ml-2 flex-shrink-0 ${
                  open ? 'rotate-180' : ''
                } text-gray-400 transition`}
              />
            </PopoverButton>

            {/* Clear chip */}
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={clear}
                className="absolute -right-2 -top-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 transition shadow-sm"
                aria-label="Clear dates"
              >
                <XMarkIcon className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-120"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            {/* KEY: responsive panel width — never overflow on phones */}
            <PopoverPanel className="absolute z-50 mt-2 left-0 sm:left-auto w-[22rem] sm:w-[24rem] max-w-[92vw] rounded-2xl bg-white shadow-xl ring-1 ring-black/5 p-3">
              {/* Presets */}
              <div className="flex flex-wrap gap-2 mb-3">
                {presets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => applyPreset(p.range)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={clear}
                  className="ml-auto px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition"
                >
                  {t('datepicker.clear')}
                </button>
              </div>

              {/* Calendar */}
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <Calendar
                  selectRange
                  onChange={handleCalendarChange}
                  value={[fromDate, toDate]}
                  minDate={minDate || undefined}
                  className="w-full text-gray-700 react-calendar-modern"
                />
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default DatePicker;
